import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Calendar, MapPin, Clock, Trophy, ChevronLeft, Users } from 'lucide-react';
import { VehicleIcon } from '../../components/VehicleIcons';
import { loadStripe } from '@stripe/stripe-js';
import '../../styles/RaceDetails.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const RaceDetails = () => {
  const { raceId } = useParams();
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();
  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDistance, setSelectedDistance] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    zipCode: '',
    city: '',
    address: '',
    comment: ''
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  useEffect(() => {
    fetchRaceDetails();
    if (user) {
      checkRegistration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, user]);

  const fetchRaceDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/virtual-races/${raceId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch race details');
      }

      const data = await response.json();
      setRace(data.race);
      if (data.race && data.race.distances && data.race.distances.length > 0) {
        setSelectedDistance(data.race.distances[0]);
      }
      if (user) {
        setFormData(prev => ({ ...prev, fullName: user.name || '' }));
      }
    } catch (err) {
      console.error('Error fetching race details:', err);
      setError('Nem sikerült betölteni a verseny adatait.');
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_URL}/api/virtual-races/${raceId}/check-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: user.email })
      });

      if (response.ok) {
        const data = await response.json();
        setIsRegistered(data.isRegistered);
      }
    } catch (err) {
      console.error('Error checking registration:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  const getVehicleIcon = (vehicleType, color = '#049A63') => {
    return <VehicleIcon name={vehicleType} size={32} color={color} />;
  };

  const getVehicleLabel = (vehicleType) => {
    switch (vehicleType) {
      case 'running': return 'Futás';
      case 'walking': return 'Séta';
      case 'biking': return 'Kerékpározás';
      default: return 'Egyéb';
    }
  };

  const isRegistrationOpen = () => {
    if (!race) return false;
    const now = new Date();
    const startDate = new Date(race.registrationStartDate);
    const endDate = new Date(race.registrationEndDate);
    const isFull = race.maxParticipants && race.currentParticipants >= race.maxParticipants;
    return race.registrationOpen && now >= startDate && now <= endDate && !isFull;
  };

  const isFull = () => {
    if (!race || !race.maxParticipants) return false;
    return race.currentParticipants >= race.maxParticipants;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = await getAccessTokenSilently();
      
      // Create registration
      const registerResponse = await fetch(`${API_URL}/api/virtual-races/${raceId}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          selectedDistance,
          shippingAddress: formData,
          userId: user.sub,
          userEmail: user.email,
          userName: user.name
        })
      });

      if (!registerResponse.ok) {
        const errorData = await registerResponse.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const { participant } = await registerResponse.json();

      // Create Stripe checkout session
      const stripe = await stripePromise;
      const checkoutResponse = await fetch(`${API_URL}/api/payments/create-race-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          raceId: race._id,
          participantId: participant._id,
          raceName: race.name,
          selectedDistance,
          price: race.price,
          email: user.email
        })
      });

      const { sessionId } = await checkoutResponse.json();
      await stripe.redirectToCheckout({ sessionId });

    } catch (err) {
      console.error('Error submitting registration:', err);
      alert(err.message || 'Hiba történt a regisztráció során');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="races-loading">
        <div className="spinner"></div>
        <p>Verseny betöltése...</p>
      </div>
    );
  }

  if (error || !race) {
    return (
      <div className="races-error">
        <p>{error || 'Verseny nem található'}</p>
        <button onClick={() => navigate('/verseny')} className="btn btn-primary">
          Vissza a versenyekhez
        </button>
      </div>
    );
  }

  if (isRegistered) {
    return (
      <div className="race-details-container">
        <button onClick={() => navigate('/verseny')} className="back-button-simple">
          <ChevronLeft size={20} />
          Vissza
        </button>
        <div className="already-registered">
          <Trophy size={64} color="#049A63" />
          <h2>Már regisztráltál erre a versenyre!</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="race-details-container">
      <button onClick={() => navigate('/verseny')} className="back-button-simple">
        <ChevronLeft size={20} />
        Vissza
      </button>

      <div className="race-details-content">
        {/* Left Column - Race Info */}
        <div className="race-info-column">
          {race.bannerImage && (
            <div className="race-banner-large">
              <img src={race.bannerImage} alt={race.name} />
            </div>
          )}
          
          <div className="race-header-section">
            <h1>{race.name}</h1>
            <div className="race-badges">
              <span className={`badge ${isRegistrationOpen() ? 'badge-open' : 'badge-closed'}`}>
                {isFull() ? 'Megtelt' : isRegistrationOpen() ? 'Nyitva' : 'Lezárva'}
              </span>
              {race.maxParticipants && (
                <span className="badge badge-participants">
                  <Users size={14} />
                  {race.currentParticipants}/{race.maxParticipants}
                </span>
              )}
            </div>
          </div>

          <div className="race-info-grid-compact">
            <div className="info-box">
              <span className="info-icon-large">{getVehicleIcon(race.vehicleType)}</span>
              <div>
                <div className="info-label">Típus</div>
                <div className="info-value">{getVehicleLabel(race.vehicleType)}</div>
              </div>
            </div>

            <div className="info-box">
              <MapPin size={20} />
              <div>
                <div className="info-label">Távok</div>
                <div className="info-value">{race.distances.map(d => `${d/1000}km`).join(' / ')}</div>
              </div>
            </div>

            <div className="info-box">
              <Clock size={20} />
              <div>
                <div className="info-label">Időtartam</div>
                <div className="info-value">{race.duration} nap</div>
              </div>
            </div>

            <div className="info-box">
              <Calendar size={20} />
              <div>
                <div className="info-label">Verseny</div>
                <div className="info-value-small">{formatDate(race.raceStartDate)} - {formatDate(race.raceEndDate)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Description & Registration */}
        <div className="registration-form-column">
          <div className="race-description-section">
            <h3>Leírás:</h3>
            <p className="race-description-full">{race.description}</p>
          </div>

          {isRegistrationOpen() && !isFull() ? (
            <div className="registration-form-section">
              <h2>Regisztráció</h2>
              
              {!showForm ? (
                <div className="registration-preview">
                  <div className="price-display">
                    <span className="price-label">Regisztrációs díj</span>
                    <span className="price-amount">{race.price.toLocaleString('hu-HU')} Ft</span>
                  </div>
                  <button 
                    onClick={() => setShowForm(true)}
                    className="btn btn-primary btn-large"
                  >
                    Regisztráció indítása
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-section">
                    <h3>Táv kiválasztása</h3>
                    <div className="distance-selector">
                      {race.distances.map(distance => (
                        <label key={distance} className={`distance-radio ${selectedDistance === distance ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="distance"
                            value={distance}
                            checked={selectedDistance === distance}
                            onChange={() => setSelectedDistance(distance)}
                          />
                          <span>{distance / 1000} km</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Szállítási adatok</h3>
                    <div className="form-grid">
                      <div className="form-group full-width">
                        <label>Teljes név *</label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          placeholder="Például: Nagy János"
                        />
                      </div>

                      <div className="form-group">
                        <label>Irányítószám *</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          required
                          pattern="[0-9]{4}"
                          placeholder="1234"
                        />
                      </div>

                      <div className="form-group">
                        <label>Város *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          placeholder="Budapest"
                        />
                      </div>

                      <div className="form-group full-width">
                        <label>Cím *</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          required
                          placeholder="Példa utca 12."
                        />
                      </div>

                      <div className="form-group full-width">
                        <label>Megjegyzés futárnak (opcionális)</label>
                        <textarea
                          name="comment"
                          value={formData.comment}
                          onChange={handleInputChange}
                          rows="2"
                          placeholder="Pl.: Csengő neve, emelet"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-summary">
                    <div className="summary-row">
                      <span>Verseny:</span>
                      <strong>{race.name}</strong>
                    </div>
                    <div className="summary-row">
                      <span>Táv:</span>
                      <strong>{selectedDistance / 1000} km</strong>
                    </div>
                    <div className="summary-row summary-total">
                      <span>Fizetendő:</span>
                      <strong>{race.price.toLocaleString('hu-HU')} Ft</strong>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-large btn-block"
                    disabled={submitting}
                  >
                    {submitting ? 'Feldolgozás...' : 'Fizetés'}
                  </button>
                  
                  <button 
                    type="button" 
                    onClick={() => setShowForm(false)}
                    className="btn btn-secondary btn-block"
                    disabled={submitting}
                  >
                    Mégse
                  </button>
                </form>
              )}
            </div>
          ) : isFull() ? (
            <div className="registration-closed">
              <p>Ez a verseny elérte a maximális résztvevői létszámot.</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RaceDetails;
